// React and router imports / Importaciones de React y router
import { Link } from 'react-router-dom';
import { memo, useMemo } from 'react';

// Icons / Iconos
import { FaInstagram } from 'react-icons/fa';

// Components / Componentes
import Logo from './common/Logo';

// Styles / Estilos
import '../styles/footer.css';

/**
 * Footer component - Displays company information, links and contact details
 * Componente Footer - Muestra información de la empresa, enlaces y detalles de contacto
 * 
 * This component is memoized to prevent unnecessary re-renders
 * Este componente está memorizado para evitar re-renderizados innecesarios
 */
const Footer = () => {
  // Get current year for copyright / Obtener año actual para copyright
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  
  // Memoize the footer content to prevent unnecessary re-renders
  // Memorizar el contenido del footer para evitar re-renderizados innecesarios
  const footerContent = useMemo(() => (
    <footer className="bg-dark text-white mt-auto footer-mobile-compact">
      <div className="container py-5 footer-mobile-spacing">
        <div className="row g-5">
          {/* Company Info */}
          <div className="col-lg-4 mb-4 mb-lg-0 text-start footer-mobile-centered footer-mobile-mb footer-mobile-col-full">
            <div className="mb-3 mb-lg-4">
              <Logo size="medium" showText={false} className="text-white" />
            </div>
            <p className="mb-3 mb-lg-4 pe-lg-4">
              Desarrollamos tu línea exclusiva de productos que complementen tu práctica para que le ofrezcas a tus pacientes salud integral.
            </p>
            <h6 className="text-white mb-2 mb-lg-3">Síguenos</h6>
            <div className="social-icons mb-2 mb-lg-3">
              <a href="https://www.instagram.com/vvhealth" 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="social-icon" aria-label="Instagram">
                <FaInstagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-lg-4 mb-4 mb-lg-0 text-start footer-mobile-centered footer-mobile-mb footer-mobile-col-2">
            <h5 className="text-white mb-3 mb-lg-4">Enlaces Rápidos</h5>
            <ul className="list-unstyled">
              <li className="mb-2 mb-lg-3">
                <Link to="/" className="text-white text-decoration-none hover-opacity">Inicio</Link>
              </li>
              <li className="mb-2 mb-lg-3">
                <Link to="/catalog" className="text-white text-decoration-none hover-opacity">Catálogo</Link>
              </li>
              <li className="mb-2 mb-lg-3">
                <Link to="/about" className="text-white text-decoration-none hover-opacity">Sobre nosotros</Link>
              </li>
              <li className="mb-2 mb-lg-3">
                <Link to="/contact" className="text-white text-decoration-none hover-opacity">Contáctanos</Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="col-lg-4 text-start footer-mobile-centered footer-mobile-col-2">
            <h5 className="text-white mb-3 mb-lg-4">Contáctanos</h5>
            <ul className="list-unstyled">
              <li className="mb-2 mb-lg-3">
                <strong className="text-white">Email:</strong><br />
                <a href="mailto:info@vvhealth.com" className="text-white text-decoration-none hover-opacity">
                  info@vvhealth.com
                </a>
              </li>
              <li className="mb-2 mb-lg-3">
                <strong className="text-white">Teléfono:</strong><br />
                <a href="tel:+17873547839" className="text-white text-decoration-none hover-opacity">
                  787-354-7839
                </a>
              </li>
              <li>
                <strong className="text-white">Dirección:</strong><br />
                <span className="text-white">San Juan, Puerto Rico</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Copyright */}
      <div className="border-top border-secondary">
        <div className="container py-3 footer-copyright-mobile">
          <div className="row">
            <div className="col-12 text-center mb-1 mb-lg-2">
              <small className="text-white-50">
                {currentYear} VV Health. Todos los derechos reservados.
              </small>
            </div>
            <div className="col-12 text-center">
              <Link to="/privacy" className="text-white-50 text-decoration-none mx-2 mx-lg-3 hover-opacity">
                <small>Privacidad</small>
              </Link>
              <Link to="/terms" className="text-white-50 text-decoration-none mx-2 mx-lg-3 hover-opacity">
                <small>Términos</small>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  ), [currentYear]);
  
  return footerContent;
};

// Memoize the component to prevent unnecessary re-renders
// Memorizar el componente para evitar renderizados innecesarios
export default memo(Footer);
